import { ArrowRight, CalendarDays, HeartHandshake, ShieldCheck, Sparkles } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FigureMotif } from '@/components/brand/FigureMotif';
import { Container } from '@/components/layout/Container';
import { Button } from '@/components/ui/button';
import { formatDate, formatMoney } from '@/lib/utils/format';
import { useImpactStats } from '@/features/donations/api';
import { useEvents } from '@/features/events/api';
import { useHomeHero } from './useHomeHero';
import { useTilt } from './useTilt';

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};
const rise = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export function Hero() {
  const hero = useHomeHero();
  const impact = useImpactStats();
  const events = useEvents({ limit: 1 });
  const reduce = useReducedMotion();
  const nextEvent = events.data?.items[0];

  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const stageY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);

  const headline = hero.headline ?? 'No man should face it';
  const highlight = hero.highlight ?? 'alone';
  const subheadline =
    hero.subheadline ??
    'Care For Him offers confidential support, resources and community for men — and the people who love them. Reaching out is strength.';

  return (
    <section ref={ref} className="relative overflow-hidden">
      {/* Ambient background */}
      <motion.div aria-hidden style={{ y: bgY }} className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-50/70 to-bg" />
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-pink-400/30 blur-3xl animate-blob" />
        <div className="absolute right-[-4rem] top-8 h-80 w-80 rounded-full bg-teal-400/25 blur-3xl animate-blob [animation-delay:-5s]" />
        <div className="absolute bottom-[-4rem] left-1/3 h-72 w-72 rounded-full bg-purple-400/25 blur-3xl animate-blob [animation-delay:-9s]" />
        <div className="absolute inset-0 bg-grid opacity-60" />
      </motion.div>

      <Container className="grid items-center gap-12 py-20 md:grid-cols-2 md:py-28">
        {/* Copy */}
        <motion.div variants={stagger} initial="hidden" animate="show">
          <motion.p
            variants={rise}
            className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-pink-500/20 bg-pink-50 px-3 py-1 text-sm font-semibold text-pink-600"
          >
            <Sparkles className="h-3.5 w-3.5" /> Men's Mental Health Initiative
          </motion.p>
          <motion.h1
            variants={rise}
            className="text-4xl font-bold leading-[1.05] text-ink md:text-6xl"
          >
            {headline}{' '}
            <span className="text-gradient-brand">{highlight}</span>.
          </motion.h1>
          <motion.p variants={rise} className="mt-5 max-w-lg text-lg text-muted">
            {subheadline}
          </motion.p>
          <motion.div variants={rise} className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/get-help">
                Get Help <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="secondary">
              <Link to="/donate">Donate</Link>
            </Button>
          </motion.div>

          {/* Real trust row */}
          <motion.div variants={rise} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3">
            <TrustStat
              value={impact.data ? formatMoney(impact.data.total_raised_minor, impact.data.currency) : '—'}
              label="raised for care"
            />
            <div className="hidden h-8 w-px bg-line sm:block" />
            <TrustStat
              value={impact.data ? impact.data.donors_count.toLocaleString() : '—'}
              label="supporters"
            />
            <div className="hidden h-8 w-px bg-line sm:block" />
            <span className="inline-flex items-center gap-1.5 text-sm text-muted">
              <ShieldCheck className="h-4 w-4 text-teal-500" /> 100% confidential
            </span>
          </motion.div>
        </motion.div>

        {/* 3D stage */}
        <motion.div style={{ y: stageY }} className="relative mx-auto w-full max-w-md">
          <HeroStage images={hero.images}>
            {impact.data && (
              <FloatCard className="-left-4 bottom-6 sm:-left-10" z={90} delay={0.2}>
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-pink-500/15 text-pink-600">
                  <HeartHandshake className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-bold text-ink">
                    {formatMoney(impact.data.total_raised_minor, impact.data.currency)}
                  </div>
                  <div className="text-xs text-muted">raised together</div>
                </div>
              </FloatCard>
            )}
            {nextEvent && (
              <FloatCard className="-right-3 top-8 sm:-right-8" z={70} delay={0.35}>
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-teal-500/15 text-teal-600">
                  <CalendarDays className="h-5 w-5" />
                </span>
                <div className="max-w-[9rem]">
                  <div className="truncate text-sm font-bold text-ink">{nextEvent.title}</div>
                  <div className="text-xs text-muted">{formatDate(nextEvent.start_at)}</div>
                </div>
              </FloatCard>
            )}
          </HeroStage>
        </motion.div>
      </Container>
    </section>
  );
}

function TrustStat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-xl font-bold text-ink">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
}

function HeroStage({ images, children }: { images: string[]; children?: React.ReactNode }) {
  const { rotateX, rotateY, onPointerMove, onPointerLeave } = useTilt(9);

  return (
    <div className="[perspective:1200px]">
      <motion.div
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="relative aspect-[4/5]"
      >
        {/* Glow behind */}
        <div
          aria-hidden
          className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-pink-400/40 via-purple-400/30 to-teal-400/40 blur-2xl"
          style={{ transform: 'translateZ(-40px)' }}
        />
        {/* Main visual card */}
        <div
          className="absolute inset-0 overflow-hidden rounded-[1.75rem] border border-white/40 shadow-[var(--shadow-elevated)] ring-1 ring-ink/5 dark:border-white/10"
          style={{ transform: 'translateZ(30px)' }}
        >
          {images.length > 0 ? <ImageShowcase images={images} /> : <BrandedScene />}
          {/* sheen */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-white/10" />
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function ImageShowcase({ images }: { images: string[] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const id = setInterval(() => setI((v) => (v + 1) % images.length), 5000);
    return () => clearInterval(id);
  }, [images.length]);

  return (
    <div className="absolute inset-0">
      <AnimatePresence mode="popLayout">
        <motion.img
          key={images[i]}
          src={images[i]}
          alt="Care For Him community"
          fetchPriority="high"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: 'easeInOut' }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
    </div>
  );
}

/** Premium branded composition shown until real hero images are uploaded. */
function BrandedScene() {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute inset-0 grid place-items-center">
        <motion.div
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="relative"
        >
          <div className="absolute -inset-8 rounded-full bg-white/20 blur-2xl" />
          <FigureMotif className="relative h-48 w-48 text-white drop-shadow-2xl" />
        </motion.div>
      </div>
      <p className="absolute bottom-5 left-0 right-0 text-center text-sm font-medium text-white/80">
        Care · Mentorship · Community
      </p>
    </div>
  );
}

function FloatCard({
  children,
  className,
  z,
  delay,
}: {
  children: React.ReactNode;
  className?: string;
  z: number;
  delay: number;
}) {
  // Depth (translateZ) lives on a static outer element so Framer Motion's
  // animated transform on the inner element can't overwrite it.
  return (
    <div className={`absolute ${className ?? ''}`} style={{ transform: `translateZ(${z}px)` }}>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="animate-float flex items-center gap-2.5 rounded-2xl glass px-3.5 py-2.5 shadow-[var(--shadow-elevated)]">
          {children}
        </div>
      </motion.div>
    </div>
  );
}
